import { apiService } from '../api';
import {
  AdmissionApplication,
  AdmissionApplicationsListResponse,
  ApplicationStatus,
  InterviewRoundScore,
  ConcessionRequest,
  DetailChange,
  ConcessionRequestList,
  ConcessionRequestStatus,
} from '../../types/admissions';
import type { BulkMarksPayloadRow } from '../../utils/bulkMarksXlsx';
import type { ApplicationSiblings } from '../peopleService';

/** Shared blob-download helper — keeps the axios auth interceptor in the loop. */

/**
 * Wraps the /api/admission-applications REST surface. All status changes
 * funnel through `/:id/transition` which calls the backend's state machine
 * (see services/admissions/transitionApplication.ts). Direct status writes
 * are not exposed.
 *
 * Field-level writes (document ops, score entry, concession request) are
 * individual endpoints — they mutate subdocs without touching `status`.
 */
export interface ApplicationListFilters {
  cycleId?: string;
  classId?: string;
  /** Comma-separated for multi-status filtering. */
  status?: ApplicationStatus | string;
  source?: string;
  admitCardState?: 'issued' | 'missing';
  search?: string;
}

export const admissionApplicationService = {
  // ─── Reads ────────────────────────────────────────────────────────────────
  list: async (params?: ApplicationListFilters & { page?: number; limit?: number }): Promise<AdmissionApplicationsListResponse> => {
    const r = await apiService.get<AdmissionApplicationsListResponse>('/admission-applications', params);
    return r.data!;
  },

  /** Marks for many applicants at once (the marks spreadsheet). Each row is
   *  saved on its own; the result says which saved and why others didn't. */
  bulkRecordMarks: async (cycleId: string, rows: BulkMarksPayloadRow[]) => {
    const r = await apiService.post<Array<{ applicationId: string; outcome: 'saved' | 'failed'; message?: string }>>(
      `/admission-applications/by-cycle/${cycleId}/bulk-marks`, { rows });
    return r.data || [];
  },

  /** Every application matching the filters, with what a full export needs
   *  (family name, linked-family guardians, staff names). Pages through the
   *  list at the API's maximum page size. */
  listAllWithDetails: async (params: ApplicationListFilters): Promise<{ items: AdmissionApplication[]; users: Record<string, string> }> => {
    const items: AdmissionApplication[] = [];
    const users: Record<string, string> = {};
    for (let page = 1; ; page += 1) {
      // eslint-disable-next-line no-await-in-loop -- pages are sequential by nature
      const r = await apiService.get<AdmissionApplicationsListResponse>('/admission-applications', { ...params, include: 'export', page, limit: 100 });
      items.push(...r.data!.items);
      Object.assign(users, r.data!.users);
      if (!r.data!.pagination.hasNext) return { items, users };
    }
  },

  get: async (id: string): Promise<AdmissionApplication> => {
    const r = await apiService.get<AdmissionApplication>(`/admission-applications/${id}`);
    return r.data!;
  },

  // ─── Corrections ─────────────────────────────────────────────────────────
  /** Correct answers (field key → value; '' clears an optional one), up to
   *  enrollment. Returns what actually changed. */
  correct: async (id: string, values: Record<string, unknown>, reason?: string, clearMarks?: boolean): Promise<{ changes: DetailChange[] }> => {
    const r = await apiService.patch<{ changes: DetailChange[] }>(`/admission-applications/${id}`, { values, reason, clearMarks });
    return r.data!;
  },

  // ─── Documents ────────────────────────────────────────────────────────────
  /** Upload one checklist document; re-uploading replaces the old copy. */
  uploadDocument: async (id: string, key: string, file: File) => {
    const form = new FormData();
    form.append('key', key);
    form.append('admissionDocument', file);
    return (await apiService.upload(`/admission-applications/${id}/documents`, form)).data;
  },

  verifyDocument: async (id: string, docId: string) =>
    (await apiService.post(`/admission-applications/${id}/documents/${docId}/verify`)).data,

  rejectDocument: async (id: string, docId: string, reason: string) =>
    (await apiService.post(`/admission-applications/${id}/documents/${docId}/reject`, { reason })).data,

  /** One email to the family listing every rejected document. */
  requestDocumentResubmission: async (id: string, message: string) =>
    (await apiService.post(`/admission-applications/${id}/documents/resend-request`, { message })).data,

  markOriginalsSeen: async (id: string, docId: string) =>
    (await apiService.post(`/admission-applications/${id}/documents/${docId}/mark-originals-seen`)).data,

  // ─── Assessment ───────────────────────────────────────────────────────────
  // Marks are validated against the cycle on the server; maximums always
  // come from the cycle, so only what was obtained is sent.
  recordTestScores: async (id: string, scores: Array<{ paperKey: string; obtained: number | null; absent: boolean }>) =>
    (await apiService.post<AdmissionApplication>(`/admission-applications/${id}/test-scores`, { scores })).data,

  recordInterviewRound: async (id: string, round: Partial<InterviewRoundScore>) =>
    (await apiService.post<AdmissionApplication>(`/admission-applications/${id}/interview-rounds`, round)).data,

  /** `null` clears a criterion. */
  recordCustomScores: async (id: string, scores: Record<string, number | null>) =>
    (await apiService.put<AdmissionApplication>(`/admission-applications/${id}/custom-scores`, { scores })).data,

  // ─── Decision exceptions (maker-checker) ─────────────────────────────────
  requestDecisionOverride: async (id: string, rationale: string) =>
    (await apiService.post<AdmissionApplication>(`/admission-applications/${id}/decision-overrides`, { rationale })).data,

  decideDecisionOverride: async (id: string, index: number, decision: 'approved' | 'rejected') =>
    (await apiService.patch<AdmissionApplication>(`/admission-applications/${id}/decision-overrides/${index}/decide`, { decision })).data,

  // ─── Concessions ──────────────────────────────────────────────────────────
  addConcessionRequest: async (id: string, payload: Partial<ConcessionRequest>) =>
    (await apiService.post(`/admission-applications/${id}/concession-requests`, payload)).data,

  /** Requests across applications by decision (Finance → Approvals). */
  listConcessionRequests: async (params: { status: ConcessionRequestStatus; page: number; limit: number }) =>
    (await apiService.get<ConcessionRequestList>('/admission-applications/concession-requests', params)).data!,

  decideConcessionRequest: async (id: string, index: number, decision: 'approved' | 'rejected') =>
    (await apiService.patch(`/admission-applications/${id}/concession-requests/${index}/decide`, { decision })).data,

  // ─── State transitions ───────────────────────────────────────────────────
  /**
   * Generic transition. Prefer the specialized endpoints (offer actions,
   * withdraw, promote-from-waitlist) which populate the right subdocs before
   * firing the transition. Use this only for simple state advances that
   * don't need subdoc setup.
   */
  transition: async (id: string, to: ApplicationStatus, options?: {
    reason?: string;
    meta?: Record<string, any>;
    /** Skip an unsettled fee step (needs admissions_fee_override). */
    feeOverride?: { reason: string };
    /** Let the applicant continue despite failing the eligibility rules
     *  (needs admissions_decisions; the reason is required). */
    waiveEligibility?: boolean;
  }) =>
    (await apiService.post(`/admission-applications/${id}/transition`, { to, ...options })).data,

  /** Bulk decision from the Decision Board. Each entry transitions through
   *  its own `transitionApplication` call server-side so one bad row never
   *  kills the batch. */
  bulkDecide: async (decisions: Array<{
    applicationId: string;
    to: ApplicationStatus;
    reason?: string;
    waitlistRank?: number;
  }>): Promise<Array<{ applicationId: string; outcome: 'transitioned' | 'noop' | 'failed'; message?: string }>> => {
    const r = await apiService.post<Array<{
      applicationId: string; outcome: 'transitioned' | 'noop' | 'failed'; message?: string;
    }>>('/admission-applications/bulk-decide', { decisions });
    return r.data || [];
  },

  // ─── Offer actions ───────────────────────────────────────────────────────
  recordOfferAcceptance: async (id: string, payload: {
    onBehalfOfGuardianId: string;
    /** Optional admission-fee heads the parent opted into (by label). */
    optionalHeadLabels?: string[];
    termsAccepted?: Array<{ termKey: string; evidenceFilename?: string }>;
  }) => (await apiService.post(`/admission-applications/${id}/offer/record-acceptance`, payload)).data,

  /** Move an open offer's last day later. */
  extendOffer: async (id: string, expiresAt: string, reason?: string) =>
    (await apiService.post<AdmissionApplication>(`/admission-applications/${id}/offer/extend`, { expiresAt, reason })).data,

  recordOfferDecline: async (id: string, reason?: string) =>
    (await apiService.post(`/admission-applications/${id}/offer/record-decline`, { reason })).data,

  recordOfferDefer: async (id: string, deferToCycleId?: string, reason?: string) =>
    (await apiService.post(`/admission-applications/${id}/offer/record-defer`, { deferToCycleId, reason })).data,

  // ─── Lifecycle ────────────────────────────────────────────────────────────
  withdraw: async (id: string, reason: string) =>
    (await apiService.post(`/admission-applications/${id}/withdraw`, { reason })).data,

  confirmForEnrollment: async (id: string, changesApplied?: Record<string, { from: any; to: any }>) =>
    (await apiService.post(`/admission-applications/${id}/enrollment-confirm`, { changesApplied: changesApplied || {} })).data,

  // ─── Resolve Family (staff flow) ─────────────────────────────────────────
  getFamilyMatches: async (id: string): Promise<FamilyMatch[]> => {
    const r = await apiService.get<FamilyMatch[]>(`/admission-applications/${id}/family-matches`);
    return r.data || [];
  },

  getSiblings: async (id: string): Promise<ApplicationSiblings> => {
    const r = await apiService.get<ApplicationSiblings>(`/admission-applications/${id}/siblings`);
    return r.data!;
  },

  resolveFamily: async (id: string, payload: { mode: 'attach' | 'create_new'; targetFamilyId?: string }): Promise<AdmissionApplication> => {
    const r = await apiService.post<AdmissionApplication>(`/admission-applications/${id}/resolve-family`, payload);
    return r.data!;
  },

  // ─── Walk-in staff create ────────────────────────────────────────────────
  createStaffApplication: async (cycleId: string, payload: StaffCreatePayload): Promise<StaffCreateResponse> => {
    const r = await apiService.post<StaffCreateResponse>(`/admission-applications/by-cycle/${cycleId}`, payload);
    return r.data!;
  },

  // ─── Bulk intake ─────────────────────────────────────────────────────────
  /**
   * Pre-check an array of applicant CNICs against existing Persons at this
   * school. Returns a map of CNIC → existing Person info (empty if the CNIC
   * is new). Called before commit so the review table can flag duplicates
   * without the operator losing their edits on submit.
   */
  precheckBulkCnics: async (
    cycleId: string,
    cnics: string[],
  ): Promise<Record<string, { grNumber: string | null; personId: string; kind: string }>> => {
    const r = await apiService.post<Record<string, { grNumber: string | null; personId: string; kind: string }>>(
      `/admission-applications/by-cycle/${cycleId}/precheck-cnics`,
      { cnics },
    );
    return r.data || {};
  },

  /**
   * Create many applications from a reviewed XLSX. Rows are processed
   * server-side in chunks of 50 (each chunk in its own transaction) so one
   * bad row doesn't take down the whole batch, but we avoid the per-row
   * session overhead.
   */
  /** Rows the cycle's eligibility rules would turn away: { rowNumber: reasons }. */
  previewBulkEligibility: async (
    cycleId: string,
    rows: Array<{ rowNumber: number; payload: Record<string, any> }>,
  ): Promise<Record<number, string[]>> => {
    const r = await apiService.post<Record<number, string[]>>(`/admission-applications/by-cycle/${cycleId}/eligibility-preview`, { rows });
    return r.data || {};
  },

  bulkCreateApplications: async (
    cycleId: string,
    rows: Array<{ rowNumber: number; payload: Record<string, any> }>,
  ): Promise<{
    summary: { total: number; created: number; failed: number };
    created: Array<{ rowNumber: number; applicationId: string; applicationNumber: string; ineligibleReasons?: string[] }>;
    failed: Array<{ rowNumber: number; error: string }>;
  }> => {
    const r = await apiService.post<{
      summary: { total: number; created: number; failed: number };
      created: Array<{ rowNumber: number; applicationId: string; applicationNumber: string; ineligibleReasons?: string[] }>;
      failed: Array<{ rowNumber: number; error: string }>;
    }>(`/admission-applications/by-cycle/${cycleId}/bulk`, { rows });
    return r.data!;
  },

  // ─── Admit cards ─────────────────────────────────────────────────────────
  issueAdmitCard: async (id: string): Promise<{ applicationId: string; rollNumber: string; reused: boolean }> => {
    const r = await apiService.post<{ applicationId: string; rollNumber: string; reused: boolean }>(
      `/admission-applications/${id}/admit-card`,
      {},
    );
    return r.data!;
  },

  bulkIssueAdmitCards: async (cycleId: string, payload?: {
    classId?: string;
  }): Promise<{
    issued: Array<{ applicationId: string; rollNumber: string; reused: boolean }>;
    skipped: Array<{ applicationId: string; reason: string }>;
  }> => {
    const r = await apiService.post<{
      issued: Array<{ applicationId: string; rollNumber: string; reused: boolean }>;
      skipped: Array<{ applicationId: string; reason: string }>;
    }>(`/admission-applications/by-cycle/${cycleId}/bulk-admit-cards`, payload || {});
    return r.data!;
  },
};

/** Match row returned by the family-matches endpoint — a candidate Family
 *  + its people + guardians + which guardian IDs matched the pending-guardian
 *  CNICs on the application. */
export interface FamilyMatch {
  family: {
    _id: string;
    displayName?: string;
    status: 'candidate' | 'resolved';
    primaryContactGuardianId?: string | null;
    feePayerGuardianId?: string | null;
  };
  people: Array<{ _id: string; grNumber?: string; name: string; kind: string }>;
  guardians: Array<{ _id: string; name: string; relation: string; phone: string; cnic: string }>;
  matchedGuardianIds: string[];
}

export interface StaffCreatePayload {
  name: string;
  fatherName: string;
  fatherPhone: string;
  address: string;
  cnic: string;
  gender: 'male' | 'female' | 'other';
  classId: string;
  candidateContact?: string;
  email?: string;
  dob?: string;
  previousSchool?: string;
  previousPercentage?: number;
  previousSubjects?: string[];
  fatherCnic?: string;
  fatherEmail?: string;
  fatherOccupation?: string;
  motherName?: string;
  motherPhone?: string;
  motherCnic?: string;
  motherEmail?: string;
  motherOccupation?: string;
  formData?: Record<string, any>;
  // Identity-reuse branches:
  personId?: string;    // re-admission: reuse an existing Person
  familyId?: string;    // sibling: attach fresh Person to existing Family
}

export interface StaffCreateResponse {
  applicationId: string;
  applicationNumber: string;
  statusToken: string;
  status: ApplicationStatus;
}

export type AdmissionApplicationServiceShape = typeof admissionApplicationService;
